import * as pdfjsLib from 'pdfjs-dist';
// Vite resolves ?url to a packaged worker path; crxjs emits it as a chunk.
import workerSrc from 'pdfjs-dist/build/pdf.worker.mjs?url';

import { hashManuscriptText } from './hash';
import { detectSections, detectTitle, sectionAt } from './sections';
import { extractAuthorSurnames, extractAffiliations } from './authors';
import { preprocessEditorialManager, surnameOf } from './editorial-manager';
import { cleanupExtractedText } from './cleanup';
import { detectTabularRegions } from './tables';
import type { ManuscriptDocument, Page, Paragraph, SectionSpan } from './types';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

// 1.1.0: Editorial Manager cover-page parsing + duplicate-body collapse.
const EXTRACTOR_VERSION = 'pdf-1.1.0';

export interface ExtractOptions {
  /** Called after each page completes; fraction is 0..1. */
  onProgress?: (fraction: number) => void;
}

export async function extractFromArrayBuffer(
  buf: ArrayBuffer,
  opts: ExtractOptions = {},
): Promise<ManuscriptDocument> {
  const doc = await pdfjsLib.getDocument({ data: buf }).promise;
  const numPages = doc.numPages;
  const pages: Page[] = [];

  for (let i = 1; i <= numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const text = pageTextFromContent(content);
    pages.push({ pageNumber: i, text });
    opts.onProgress?.(i / numPages);
  }

  return buildDocument(pages);
}

/**
 * Build a ManuscriptDocument from pasted plain text — the scanned-PDF fallback
 * path (PRD §6 acceptance criterion). The whole text becomes a single page; the
 * rest of the pipeline (section detection, hashing, classification) is identical.
 */
export async function extractFromText(rawText: string): Promise<ManuscriptDocument> {
  const pages: Page[] = [{ pageNumber: 1, text: rawText }];
  return buildDocument(pages);
}

async function buildDocument(pages: Page[]): Promise<ManuscriptDocument> {
  const joined = pages.map((p) => p.text).join('\n');
  // OMJ uploads are Editorial Manager PDFs: strip the structured cover page
  // and collapse the duplicated (clean + line-numbered) body so the pipeline
  // analyses one real manuscript, not cover-metadata + 2× body.
  const em = preprocessEditorialManager(joined);
  // P2-9: strip repeated running heads/footers/page numbers and line-number
  // gutters (incl. manuscripts uploaded as only a line-numbered copy, where
  // the EM duplicate-collapse had no clean twin to keep). Self-disabling and
  // safety-netted: a clean PDF passes through unchanged.
  const rawText = cleanupExtractedText(em.cleanedText).text;
  const sections = detectSections(rawText);
  const heuristicTitle = detectTitle(rawText, sections);
  const title =
    em.cover?.fullTitle && em.cover.fullTitle.length >= 8
      ? em.cover.fullTitle
      : heuristicTitle;
  // pages stays the original array → correct "N pp" count. Paragraph
  // page-numbers become approximate once the cover/duplicate is removed;
  // section + content accuracy matters far more to an editor than exact
  // page refs, which is the deliberate trade here.
  const paragraphs = extractParagraphs(rawText, pages, sections);
  const wordCount = rawText.split(/\s+/).filter(Boolean).length;
  const looksScanned = rawText.replace(/\s+/g, '').length < 200 && pages.length >= 1;
  const contentHash = await hashManuscriptText(rawText);
  const stub: ManuscriptDocument = {
    contentHash,
    title,
    authorSurnames: [],
    affiliations: [],
    rawText,
    pages,
    paragraphs,
    sections,
    wordCount,
    looksScanned,
    extractorVersion: EXTRACTOR_VERSION,
    emCover: em.cover,
    articleType: em.cover?.articleType ?? null,
    tableRegions: detectTabularRegions(rawText),
  };
  // Authoritative author/affiliation lists come from the EM cover when
  // present (the body may be anonymised for double-blind review); fall back
  // to body heuristics otherwise.
  const coverSurnames = [
    ...new Set(
      (em.cover?.authors ?? [])
        .map(surnameOf)
        .filter((s): s is string => Boolean(s)),
    ),
  ];
  const authorSurnames =
    coverSurnames.length > 0 ? coverSurnames : extractAuthorSurnames(stub);
  // Union authoritative cover affiliations with any the body byline yields
  // (best-effort co-author coverage for the reviewer COI filter).
  const affiliations = [
    ...new Set(
      [...(em.cover?.affiliations ?? []), ...extractAffiliations(stub)]
        .map((a) => a.trim())
        .filter((a) => a.length > 0),
    ),
  ].slice(0, 20);
  return { ...stub, authorSurnames, affiliations };
}

interface TextItemLike {
  str?: string;
  hasEOL?: boolean;
}

function pageTextFromContent(content: { items: unknown[] }): string {
  const lines: string[] = [];
  let current = '';
  for (const raw of content.items) {
    const item = raw as TextItemLike;
    if (typeof item.str !== 'string') continue;
    current += item.str;
    if (item.hasEOL) {
      lines.push(current);
      current = '';
    } else {
      current += ' ';
    }
  }
  if (current.trim().length > 0) lines.push(current);
  return lines.map((l) => l.replace(/\s+/g, ' ').trim()).join('\n');
}

function extractParagraphs(
  rawText: string,
  pages: Page[],
  sections: SectionSpan[],
): Paragraph[] {
  // Page-start offsets in rawText, given pages are joined with '\n'.
  const pageStartOffsets: number[] = [];
  let off = 0;
  for (const p of pages) {
    pageStartOffsets.push(off);
    off += p.text.length + 1;
  }

  function pageAt(offset: number): number {
    let lo = 0;
    let hi = pageStartOffsets.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (pageStartOffsets[mid] <= offset) lo = mid;
      else hi = mid - 1;
    }
    return lo + 1;
  }

  // A "paragraph" in Sprint 1 is a non-empty line. PDFs don't preserve real
  // paragraph structure without layout analysis; pattern locators in Sprint 3
  // can recombine consecutive lines as needed.
  const paragraphs: Paragraph[] = [];
  const lineRe = /[^\n]+/g;
  let m: RegExpExecArray | null;
  while ((m = lineRe.exec(rawText)) !== null) {
    const text = m[0].trim();
    if (text.length === 0) continue;
    const offset = m.index;
    paragraphs.push({
      text,
      section: sectionAt(sections, offset),
      offset,
      pageNumber: pageAt(offset),
    });
  }
  return paragraphs;
}
